(() => {
  const CONTAINER_ID = "optionPlusvariant-container";
  const SWATCH_SECTION_SELECTOR =
    '.variant-section[data-variant-type="ImgSwatches"]';
  const SWATCH_SELECTOR = ".image-swatch-wrapper";
  const DROPDOWN_SELECTOR = ".image-searchable-dropdown";
  const DROPDOWN_OPTION_SELECTOR = ".dropdown-option";
  const SELECTED_CLASS = "is-selected-swatch";
  const HIDE_URL_CLASS = "op-hide-image-url";

  function isImageUrl(value) {
    if (!value) return false;
    return /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i.test(value.trim());
  }

  function normalizeImageUrl(url) {
    if (!url) return "";
    try {
      const parsed = new URL(url, window.location.origin);
      const filename = parsed.pathname.split("/").pop() || "";
      return filename
        .replace(/\.(jpe?g|png|gif|webp|avif|svg)$/i, "")
        .replace(/_(?:\d+x\d*|\d*x\d+)(?:_[a-z_]+)?$/i, "")
        .toLowerCase();
    } catch (e) {
      return url.toLowerCase();
    }
  }

  function getProductInfo(container) {
    return container.closest("product-info") || document;
  }

  function getActiveMediaImages(scope) {
    const galleries = scope.querySelectorAll("media-gallery");
    const images = [];
    galleries.forEach((gallery) => {
      const activeSlide = gallery.querySelector(
        '[id^="GalleryViewer"] [data-media-id].is-active',
      );
      const img = activeSlide?.querySelector(
        ".product__modal-opener--image img, .product-media-container img",
      );
      if (img) images.push(img);
    });
    return images;
  }

  function findMatchingThumbnails(scope, imageUrl) {
    const needle = normalizeImageUrl(imageUrl);
    if (!needle) return [];

    const matches = [];
    scope.querySelectorAll("media-gallery").forEach((gallery) => {
      const item = Array.from(
        gallery.querySelectorAll('[id^="GalleryThumbnails"] .pf-thumb-item'),
      ).find((el) => {
        const img = el.querySelector("img");
        const src =
          img?.currentSrc || img?.src || img?.getAttribute("src") || "";
        return normalizeImageUrl(src) === needle;
      });
      if (item) matches.push({ gallery, target: item.dataset.target });
    });
    return matches;
  }

  function updateGalleryImageDirectly(container, imageUrl) {
    const scope = getProductInfo(container);
    const images = getActiveMediaImages(scope);
    if (!images.length) return false;

    images.forEach((img) => {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = imageUrl;
    });
    return true;
  }
  function applyImageToGallery(container, imageUrl) {
    const scope = getProductInfo(container);
    const matches = findMatchingThumbnails(scope, imageUrl);
    matches.forEach(({ gallery, target }) =>
      gallery.setActiveMedia?.(target, false),
    );
    if (!matches.length) updateGalleryImageDirectly(container, imageUrl);
  }

  function markSelected(section, selector, chosen) {
    section.querySelectorAll(`${selector}.${SELECTED_CLASS}`).forEach((el) => {
      if (el !== chosen) el.classList.remove(SELECTED_CLASS);
    });
    chosen.classList.add(SELECTED_CLASS);
  }

  function getOptionLabel(option) {
    const named = option.querySelector(".image-name");
    if (named && named.textContent.trim()) return named.textContent.trim();

    const img = option.querySelector("img[alt]");
    if (img && img.alt.trim()) return img.alt.trim();

    const text = Array.from(option.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent.trim())
      .filter(Boolean)
      .join(" ");
    if (text && !isImageUrl(text)) return text;

    return "";
  }
  function getLabelForUrl(dropdown, url) {
    if (!url) return "";
    const needle = normalizeImageUrl(url);
    const match = Array.from(
      dropdown.querySelectorAll(DROPDOWN_OPTION_SELECTOR),
    ).find(
      (opt) =>
        opt.dataset.value && normalizeImageUrl(opt.dataset.value) === needle,
    );
    return match ? getOptionLabel(match) : "";
  }

  function fixDropdownDisplay(dropdown) {
    const searchInput = dropdown.querySelector(
      ".dropdown-header .dropdown-search",
    );
    if (searchInput && isImageUrl(searchInput.value.trim())) {
      const label = getLabelForUrl(dropdown, searchInput.value.trim());
      if (label) searchInput.value = label;
    }

    dropdown.querySelectorAll(".selected-values-display").forEach((box) => {
      const text =
        box.querySelector(".selected-values-text")?.textContent.trim() ||
        box.textContent.trim();
      if (isImageUrl(text.replace(/^selected\s*:/i, "").trim())) {
        box.classList.add(HIDE_URL_CLASS);
      }
    });
  }

  let suppressionCount = 0;
  let originalFocus,
    originalScrollIntoView,
    originalWindowScrollTo,
    originalWindowScrollBy;

  function suppressScrolling(durationMs) {
    const x = window.scrollX;
    const y = window.scrollY;

    if (suppressionCount === 0) {
      originalFocus = HTMLElement.prototype.focus;
      originalScrollIntoView = Element.prototype.scrollIntoView;
      originalWindowScrollTo = window.scrollTo;
      originalWindowScrollBy = window.scrollBy;

      HTMLElement.prototype.focus = function focus(options) {
        return originalFocus.call(this, { ...options, preventScroll: true });
      };
      Element.prototype.scrollIntoView = function scrollIntoView() {};
      window.scrollTo = function scrollTo() {};
      window.scrollBy = function scrollBy() {};
    }
    suppressionCount += 1;

    let active = true;
    const restore = () => {
      if (active && (window.scrollX !== x || window.scrollY !== y)) {
        originalWindowScrollTo.call(window, x, y);
      }
    };
    const onScroll = () => restore();
    window.addEventListener("scroll", onScroll, { passive: true });

    const start = performance.now();
    const tick = (now) => {
      restore();
      if (active && now - start < durationMs)
        window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);

    window.setTimeout(() => {
      active = false;
      window.removeEventListener("scroll", onScroll);

      suppressionCount -= 1;
      if (suppressionCount === 0) {
        HTMLElement.prototype.focus = originalFocus;
        Element.prototype.scrollIntoView = originalScrollIntoView;
        window.scrollTo = originalWindowScrollTo;
        window.scrollBy = originalWindowScrollBy;
      }
    }, durationMs);
  }

  function handleSwatchClick(wrapper, container) {
    const imageUrl = wrapper.dataset.value;
    if (!isImageUrl(imageUrl)) return;

    const section =
      wrapper.closest(SWATCH_SECTION_SELECTOR) || wrapper.parentElement;
    applyImageToGallery(container, imageUrl);
    if (section) markSelected(section, SWATCH_SELECTOR, wrapper);
  }

  function handleDropdownOptionClick(option, container) {
    const imageUrl = option.dataset.value;
    if (!isImageUrl(imageUrl)) return;

    const section =
      option.closest(SWATCH_SECTION_SELECTOR) || option.parentElement;
    const dropdown = option.closest(DROPDOWN_SELECTOR);

    applyImageToGallery(container, imageUrl);
    if (section) markSelected(section, DROPDOWN_OPTION_SELECTOR, option);

    if (dropdown) {
      fixDropdownDisplay(dropdown);

      let ticks = 0;
      const reapply = () => {
        fixDropdownDisplay(dropdown);
        ticks += 1;
        if (ticks < 20) window.requestAnimationFrame(reapply);
      };
      window.requestAnimationFrame(reapply);
    }
  }

  function handleClick(evt, container) {
    const swatch = evt.target.closest(SWATCH_SELECTOR);
    if (swatch && container.contains(swatch)) {
      handleSwatchClick(swatch, container);
      return;
    }

    const option = evt.target.closest(DROPDOWN_OPTION_SELECTOR);
    if (
      option &&
      container.contains(option) &&
      option.closest(SWATCH_SECTION_SELECTOR)
    ) {
      handleDropdownOptionClick(option, container);
    }
  }

  function attach(container) {
    if (container.dataset.imgSwatchesInit) return;
    container.dataset.imgSwatchesInit = "true";

    ["pointerdown", "mousedown", "touchstart"].forEach((type) => {
      container.addEventListener(
        type,
        (evt) => {
          const swatch = evt.target.closest(SWATCH_SELECTOR);
          const option = evt.target.closest(DROPDOWN_OPTION_SELECTOR);
          if (!swatch && !(option && option.closest(SWATCH_SECTION_SELECTOR)))
            return;
          suppressScrolling(600);
        },
        true,
      );
    });

    container.addEventListener("click", (evt) => handleClick(evt, container));

    const fixAllDropdowns = () => {
      container
        .querySelectorAll(`${SWATCH_SECTION_SELECTOR} ${DROPDOWN_SELECTOR}`)
        .forEach(fixDropdownDisplay);
    };
    fixAllDropdowns();
    new MutationObserver(fixAllDropdowns).observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["value", "style"],
    });
  }

  function init() {
    const existing = document.getElementById(CONTAINER_ID);
    if (existing) {
      attach(existing);
      return;
    }

    const bodyObserver = new MutationObserver(() => {
      const container = document.getElementById(CONTAINER_ID);
      if (container) {
        bodyObserver.disconnect();
        attach(container);
      }
    });
    bodyObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
